import React, { useState, useEffect, useMemo } from 'react';
import { createPortal } from 'react-dom';
import { Personnel } from '../../../../shared/types/game';
import { PersonnelConfig, Competence } from '../../../../shared/types/config';
import { getApiBaseUrl } from '../../../../core/config/api';
import { getServiceIdFromBuildingType } from '../../utils/buildingServiceMapping';
import { useSelector, useDispatch } from 'react-redux';
import { RootState } from '../../../../core/store/store';
import { updateUser } from '../../../../core/store/slices/userSlice';
import { statuts } from '../../../../config';
import usePageTranslation from '../../../../shared/hooks/usePageTranslation';
import { isTerminalInterventionInstanceStatutRaw } from '../../../../shared/constants/interventionInstanceStatus';
import OperationalPlanningModal, {
  PLANNING_GROUPE_KEYS,
  normalizePlanningGroupeValue,
  type PlanningGroupeKey,
} from '../OperationalPlanningModal';

interface BuildingPersonnelTabProps {
  personnel: Personnel[];
  loadingPersonnel: boolean;
  buildingType: string;
  buildingId: number;
  buildingName?: string;
  userId: number;
  onPersonnelAdded?: () => void;
}

const BuildingPersonnelTab: React.FC<BuildingPersonnelTabProps> = ({ 
  personnel, 
  loadingPersonnel,
  buildingType,
  buildingId,
  buildingName = '',
  userId,
  onPersonnelAdded
}) => {
  const { currentUser } = useSelector((state: RootState) => state.user);
  const dispatch = useDispatch();
  const [showOperationalPlanningModal, setShowOperationalPlanningModal] = useState(false);
  const [showAddModal, setShowAddModal] = useState(false);
  const [personnelConfigs, setPersonnelConfigs] = useState<PersonnelConfig[]>([]);
  const [loadingConfigs, setLoadingConfigs] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [isRecruiting, setIsRecruiting] = useState(false);
  const [recruitError, setRecruitError] = useState<string | null>(null);
  const [recruitSuccess, setRecruitSuccess] = useState<string | null>(null);
  const [personnelSearchQuery, setPersonnelSearchQuery] = useState('');
  const [personnelFilterActif, setPersonnelFilterActif] = useState<string>('all'); // 'all', 'actif', 'inactif'
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage] = useState(20);
  const [viewMode, setViewMode] = useState<'compact' | 'detailed'>('compact');
  const [selectedPersonnel, setSelectedPersonnel] = useState<Personnel | null>(null);
  const [showPersonnelDetailsModal, setShowPersonnelDetailsModal] = useState(false);
  const [personnelCompetences, setPersonnelCompetences] = useState<Competence[]>([]);
  const [gradeDefaultCompetences, setGradeDefaultCompetences] = useState<Competence[]>([]);
  const [loadingCompetences, setLoadingCompetences] = useState(false);
  const [competenceSearchQuery, setCompetenceSearchQuery] = useState('');
  const [posteNom, setPosteNom] = useState<string | null>(null);
  const [statutGeneral, setStatutGeneral] = useState<string | null>(null);
  const [statutPlanning, setStatutPlanning] = useState<string | null>(null);
  const [derniereIntervention, setDerniereIntervention] = useState<{ date: string; heure: string } | null>(null);
  const [personnelGroupeSelect, setPersonnelGroupeSelect] = useState<'' | PlanningGroupeKey>('');
  const [savingGroupe, setSavingGroupe] = useState(false);
  const [editingNom, setEditingNom] = useState(false);
  const [nomValue, setNomValue] = useState('');
  const [prenomValue, setPrenomValue] = useState('');
  const [savingNom, setSavingNom] = useState(false);

  const { t } = usePageTranslation('game', 'Map');

  // Obtenir l'ID du service correspondant au type de bâtiment
  const serviceId = getServiceIdFromBuildingType(buildingType);

  // Filtrer le personnel avec useMemo pour optimiser les performances
  const filteredPersonnel = useMemo(() => {
    return personnel.filter((p) => {
      // Filtre par recherche (nom, prénom, grade)
      const searchLower = personnelSearchQuery.toLowerCase().trim();
      if (searchLower && !(
        p.nom.toLowerCase().includes(searchLower) ||
        p.prenom.toLowerCase().includes(searchLower) ||
        p.grade.toLowerCase().includes(searchLower)
      )) {
        return false;
      }

      // Filtre par statut actif/inactif
      if (personnelFilterActif === 'actif' && !p.actif) return false;
      if (personnelFilterActif === 'inactif' && p.actif) return false;

      return true;
    });
  }, [personnel, personnelSearchQuery, personnelFilterActif]);

  // Pagination
  const totalPages = Math.ceil(filteredPersonnel.length / itemsPerPage);
  const paginatedPersonnel = useMemo(() => {
    const startIndex = (currentPage - 1) * itemsPerPage;
    const endIndex = startIndex + itemsPerPage;
    return filteredPersonnel.slice(startIndex, endIndex);
  }, [filteredPersonnel, currentPage, itemsPerPage]);

  // Réinitialiser la page quand le filtre change
  useEffect(() => {
    setCurrentPage(1);
  }, [personnelSearchQuery, personnelFilterActif]);

  // Fonction pour obtenir les couleurs d'un statut général
  const getStatutGeneralColors = (statutNom: string | null) => {
    if (!statutNom) {
      return { backgroundColor: '#6b7280', textColor: '#fff' }; // Gris par défaut
    }
    const statut = statuts.statuts.find(s => s.nom_statut === statutNom.toUpperCase());
    return statut?.couleurs || { backgroundColor: '#6b7280', textColor: '#fff' };
  };

  // Fonction pour obtenir les couleurs d'un statut planning
  const getStatutPlanningColors = (statutNom: string | null) => {
    if (!statutNom) {
      return { backgroundColor: '#6b7280', textColor: '#fff' }; // Gris par défaut
    }
    const statut = statuts.statuts_planning.find(
      (s) => s.nom.toUpperCase() === statutNom.toUpperCase()
    );
    return statut?.couleurs || { backgroundColor: '#6b7280', textColor: '#fff' };
  };

  // Fonction pour ouvrir la modale de détails
  const handleOpenPersonnelDetails = async (personnel: Personnel) => {
    setSelectedPersonnel(personnel);
    setNomValue(personnel.nom);
    setPrenomValue(personnel.prenom);
    setEditingNom(false);
    setShowPersonnelDetailsModal(true);
    setLoadingCompetences(true);
    setPersonnelCompetences([]);
    setGradeDefaultCompetences([]);
    setCompetenceSearchQuery('');
    setPosteNom(null);
    setStatutGeneral(null);
    setStatutPlanning(null);
    setDerniereIntervention(null);
    setPersonnelGroupeSelect(normalizePlanningGroupeValue(personnel.groupe) ?? '');
    setSavingGroupe(false);

    const token = localStorage.getItem('token');
    if (!token) {
      setLoadingCompetences(false);
      return;
    }

    try {
      // Charger les compétences du personnel (directes)
      const personnelResponse = await fetch(`${getApiBaseUrl(true)}/buildings/personnel/${personnel.id}/competences`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      if (personnelResponse.ok) {
        const personnelData = await personnelResponse.json();
        if (personnelData.success && Array.isArray(personnelData.data)) {
          setPersonnelCompetences(personnelData.data);
        }
      }

      // Charger le poste à responsabilité depuis le bâtiment
      // Si le personnel a un poste_id, on peut le récupérer depuis les postes du bâtiment
      if ((personnel as any).poste_id && buildingId) {
        try {
          // Récupérer les postes du bâtiment pour trouver le nom du poste assigné
          const postesResponse = await fetch(`${getApiBaseUrl(true)}/buildings/${buildingId}/postes`, {
            headers: {
              'Authorization': `Bearer ${token}`
            }
          });

          if (postesResponse.ok) {
            const postesData = await postesResponse.json();
            if (postesData.success && Array.isArray(postesData.data)) {
              const poste = postesData.data.find((p: any) => p.id === (personnel as any).poste_id);
              if (poste && poste.poste_type_nom) {
                setPosteNom(poste.poste_type_nom);
              }
            }
          }
        } catch (error) {
          console.error('Erreur lors du chargement du poste:', error);
        }
      }

      // Charger les statuts (général et planning) depuis les données du personnel ou l'API
      // D'abord vérifier si les statuts sont déjà dans les données du personnel
      if ((personnel as any).statut_nom) {
        setStatutGeneral((personnel as any).statut_nom);
      } else {
        // Par défaut si pas défini
        setStatutGeneral('DISPONIBLE');
      }

      if ((personnel as any).planning_statut_nom) {
        setStatutPlanning((personnel as any).planning_statut_nom);
      } else {
        // Par défaut si pas défini
        setStatutPlanning('PERSONNELS');
      }

      // Essayer de charger depuis l'API si l'endpoint existe (optionnel, pour mise à jour)
      try {
        const detailsResponse = await fetch(`${getApiBaseUrl(true)}/buildings/personnel/${personnel.id}/details`, {
          headers: {
            'Authorization': `Bearer ${token}`
          }
        });

        if (detailsResponse.ok) {
          const detailsData = await detailsResponse.json();
          if (detailsData.success && detailsData.data) {
            if (detailsData.data.statut_nom) {
              setStatutGeneral(detailsData.data.statut_nom);
            }
            if (detailsData.data.planning_statut_nom) {
              setStatutPlanning(detailsData.data.planning_statut_nom);
            }
            if (Object.prototype.hasOwnProperty.call(detailsData.data, 'groupe')) {
              setPersonnelGroupeSelect(
                normalizePlanningGroupeValue(
                  detailsData.data.groupe as string | null | undefined
                ) ?? ''
              );
            }
          }
        }
      } catch (error) {
        // Ignorer les erreurs si l'endpoint n'existe pas, on utilise déjà les valeurs par défaut
        console.debug('Endpoint de détails non disponible, utilisation des données du personnel');
      }

      // Charger la date/heure de la dernière intervention terminée
      // Une intervention commence quand le personnel passe en EN ROUTE et se termine quand il repasse en DISPONIBLE
      // On vérifie d'abord si le personnel a un champ derniere_intervention_date stocké
      if ((personnel as any).derniere_intervention_date) {
        try {
          const dateObj = new Date((personnel as any).derniere_intervention_date);
          setDerniereIntervention({
            date: dateObj.toLocaleDateString('fr-FR', { day: '2-digit', month: '2-digit', year: 'numeric' }),
            heure: dateObj.toLocaleTimeString('fr-FR', { hour: '2-digit', minute: '2-digit' })
          });
        } catch (e) {
          // Ignorer les erreurs de parsing de date
        }
      }
      
      // Si le personnel est actuellement en DISPONIBLE, on peut utiliser updated_at comme date de fin de dernière intervention
      // (car cela indique quand il est repassé en DISPONIBLE)
      if (!derniereIntervention && statutGeneral === 'DISPONIBLE' && (personnel as any).updated_at) {
        try {
          const dateObj = new Date((personnel as any).updated_at);
          setDerniereIntervention({
            date: dateObj.toLocaleDateString('fr-FR', { day: '2-digit', month: '2-digit', year: 'numeric' }),
            heure: dateObj.toLocaleTimeString('fr-FR', { hour: '2-digit', minute: '2-digit' })
          });
        } catch (e) {
          // Ignorer les erreurs de parsing de date
        }
      }
      
      // Sinon, chercher dans les interventions terminées où le personnel était assigné
      if (!derniereIntervention) {
        try {
          const interventionsResponse = await fetch(`${getApiBaseUrl(true)}/buildings/interventions`, {
            headers: {
              'Authorization': `Bearer ${token}`
            }
          });

          if (interventionsResponse.ok) {
            const interventionsData = await interventionsResponse.json();
            
            if (interventionsData.success && Array.isArray(interventionsData.data)) {
              // Filtrer les interventions terminées où ce personnel était assigné
              const interventionsTerminees = interventionsData.data.filter((intervention: any) => {
                // Vérifier si le personnel est dans la liste des personnels assignés
                let personnelIds: number[] = [];
                if (intervention.personnel_ids && Array.isArray(intervention.personnel_ids)) {
                  personnelIds = intervention.personnel_ids;
                } else if (intervention.personnels && Array.isArray(intervention.personnels)) {
                  personnelIds = intervention.personnels.map((p: any) => typeof p === 'number' ? p : p.id);
                } else if (intervention.personnel_id) {
                  personnelIds = [intervention.personnel_id];
                }
                
                if (!personnelIds.includes(personnel.id)) {
                  return false;
                }
                
                // Une intervention est terminée si le statut est TERMINEE ou si elle a une date_fin
                const statut = intervention.statut || '';
                const hasDateFin = intervention.date_fin || intervention.date_fin_intervention;
                return isTerminalInterventionInstanceStatutRaw(statut) || hasDateFin;
              });

              // Trier par date de fin (la plus récente en premier)
              interventionsTerminees.sort((a: any, b: any) => {
                const dateA = a.date_fin || a.date_fin_intervention || a.updated_at || a.created_at || '';
                const dateB = b.date_fin || b.date_fin_intervention || b.updated_at || b.created_at || '';
                return new Date(dateB).getTime() - new Date(dateA).getTime();
              });

              // Prendre la première (la plus récente)
              if (interventionsTerminees.length > 0) {
                const lastIntervention = interventionsTerminees[0];
                
                // Prendre la date de fin de l'intervention (quand le personnel est repassé en DISPONIBLE)
                const dateFin = lastIntervention.date_fin || lastIntervention.date_fin_intervention || lastIntervention.updated_at;
                
                if (dateFin) {
                  const dateObj = new Date(dateFin);
                  setDerniereIntervention({
                    date: dateObj.toLocaleDateString('fr-FR', { day: '2-digit', month: '2-digit', year: 'numeric' }),
                    heure: dateObj.toLocaleTimeString('fr-FR', { hour: '2-digit', minute: '2-digit' })
                  });
                }
              }
            }
          }
        } catch (error) {
          console.debug('Erreur lors du chargement de la dernière intervention:', error);
        }
      }

      // Charger les compétences par défaut du grade depuis la configuration
      const configResponse = await fetch(`${getApiBaseUrl(true)}/config/personnel`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      if (configResponse.ok) {
        const configData = await configResponse.json();
        if (configData.success && Array.isArray(configData.data)) {
          // Trouver la configuration correspondant au grade du personnel
          const gradeConfig = configData.data.find((config: PersonnelConfig) => 
            config.grade === personnel.grade || config.id === (personnel as any).grade_config_id
          );

          if (gradeConfig && gradeConfig.id_competences && gradeConfig.id_competences.length > 0) {
            // Charger les détails des compétences par défaut du grade
            const competencesResponse = await fetch(`${getApiBaseUrl(true)}/config/competences`, {
              headers: {
                'Authorization': `Bearer ${token}`
              }
            });

            if (competencesResponse.ok) {
              const competencesData = await competencesResponse.json();
              if (competencesData.success && Array.isArray(competencesData.data)) {
                // Filtrer les compétences qui correspondent aux IDs du grade
                const gradeCompetences = competencesData.data.filter((comp: Competence) =>
                  gradeConfig.id_competences.includes(comp.id)
                );
                setGradeDefaultCompetences(gradeCompetences);
              }
            }
          }
        }
      }
    } catch (error) {
      console.error('Erreur lors du chargement des compétences:', error);
    } finally {
      setLoadingCompetences(false);
    }
  };

  const handleSaveNom = async () => {
    if (!selectedPersonnel || !nomValue.trim() || !prenomValue.trim()) {
      return;
    }

    if (nomValue.trim() === selectedPersonnel.nom && prenomValue.trim() === selectedPersonnel.prenom) {
      setEditingNom(false);
      return;
    }

    setSavingNom(true);
    const token = localStorage.getItem('token');
    
    try {
      const response = await fetch(`${getApiBaseUrl(true)}/buildings/personnel/${selectedPersonnel.id}`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({ nom: nomValue.trim(), prenom: prenomValue.trim() })
      });

      const data = await response.json();

      if (response.ok && data.success) {
        // Mettre à jour le personnel dans la liste locale
        setSelectedPersonnel({ ...selectedPersonnel, nom: nomValue.trim(), prenom: prenomValue.trim() });
        setEditingNom(false);
        // Recharger la liste du personnel
        if (onPersonnelAdded) {
          onPersonnelAdded();
        }
      } else {
        alert(data.error || 'Erreur lors de la sauvegarde du nom');
      }
    } catch (error) {
      console.error('Erreur lors de la sauvegarde du nom:', error);
      alert('Erreur lors de la sauvegarde du nom');
    } finally {
      setSavingNom(false);
    }
  };

  const handleCancelEditNom = () => {
    if (selectedPersonnel) {
      setNomValue(selectedPersonnel.nom);
      setPrenomValue(selectedPersonnel.prenom);
    }
    setEditingNom(false);
  };

  const savePersonnelGroupe = async (value: '' | PlanningGroupeKey) => {
    if (!selectedPersonnel || savingGroupe) return;
    const prev = normalizePlanningGroupeValue(selectedPersonnel.groupe) ?? '';
    if (value === prev) return;

    setPersonnelGroupeSelect(value);
    setSavingGroupe(true);
    const token = localStorage.getItem('token');
    if (!token) {
      setPersonnelGroupeSelect(prev);
      setSavingGroupe(false);
      return;
    }

    try {
      const response = await fetch(`${getApiBaseUrl(true)}/buildings/personnel/${selectedPersonnel.id}`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({
          nom: selectedPersonnel.nom,
          prenom: selectedPersonnel.prenom,
          groupe: value === '' ? null : value,
        }),
      });
      const data = await response.json().catch(() => ({}));

      if (response.ok && data.success && data.data) {
        const updated = data.data as Personnel;
        setSelectedPersonnel({
          ...selectedPersonnel,
          groupe: updated.groupe ?? (value === '' ? null : value),
        });
        onPersonnelAdded?.();
      } else {
        setPersonnelGroupeSelect(prev);
        alert((data as { error?: string }).error || t('operationalPlanning.personnelDetailGroupeSaveError'));
      }
    } catch {
      setPersonnelGroupeSelect(prev);
      alert(t('operationalPlanning.personnelDetailGroupeSaveError'));
    } finally {
      setSavingGroupe(false);
    }
  };

  // Composant mémorisé pour chaque carte de personnel
  const PersonnelCard = React.memo<{ p: Personnel; compact: boolean; onOpenDetails: (p: Personnel) => void }>(({ p, compact, onOpenDetails }) => {
    if (compact) {
      // Vue compacte en format carré/bloc
      return (
        <div className="bg-gray-800 border border-gray-700 rounded-lg p-3 hover:border-blue-500 transition-colors aspect-square flex flex-col items-center justify-center relative group">
          {/* Badge actif/inactif en haut à droite */}
          <div className="absolute top-2 right-2">
            <span className={`px-1.5 py-0.5 rounded-full text-xs ${
              p.actif
                ? 'bg-green-500/20 text-green-400'
                : 'bg-gray-700 text-gray-400'
            }`}>
              {p.actif ? '✓' : '○'}
            </span>
          </div>
          
          {/* Image du grade au centre */}
          <div className="flex-shrink-0 mb-2">
            {p.grade_img ? (
              <img 
                src={p.grade_img} 
                alt={p.grade || 'Grade'} 
                className="h-12 w-12 object-contain mx-auto"
                onError={(e) => {
                  (e.target as HTMLImageElement).style.display = 'none';
                }}
              />
            ) : (
              <div className="h-12 w-12 bg-red-600 rounded flex items-center justify-center text-white font-bold text-sm mx-auto">
                ?
              </div>
            )}
          </div>

          {/* Nom et prénom */}
          <h4 className="text-sm font-semibold text-white text-center truncate w-full px-2 mb-1">
            {p.prenom} {p.nom}
          </h4>

          {/* Grade */}
          <span className="text-xs text-blue-400 text-center mb-1">
            {p.grade}
          </span>

          {/* Âge */}
          <span className="text-xs text-gray-400 text-center mb-2">
            {p.age} ans
          </span>

          {/* Bonus (affichage compact) */}
          {p.bonus && p.bonus.length > 0 && (
            <div className="mt-auto pt-2 border-t border-gray-700 w-full">
              <div className="text-center">
                <span className="px-2 py-1 bg-purple-500/20 text-purple-400 rounded text-xs">
                  {p.bonus.length} bonus{p.bonus.length > 1 ? 's' : ''}
                </span>
              </div>
            </div>
          )}

          {/* Menu options (affiché au hover) */}
          <button 
            onClick={(e) => {
              e.stopPropagation();
              onOpenDetails(p);
            }}
            className="absolute top-2 left-2 text-gray-400 hover:text-white transition-colors opacity-0 group-hover:opacity-100"
            title="Voir les détails"
          >
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 5v.01M12 12v.01M12 19v.01M12 6a1 1 0 110-2 1 1 0 010 2zm0 7a1 1 0 110-2 1 1 0 010 2zm0 7a1 1 0 110-2 1 1 0 010 2z" />
            </svg>
          </button>
        </div>
      );
    }

    return (
      <div className="bg-gray-800 border border-gray-700 rounded-lg p-4 hover:border-blue-500 transition-colors">
        <div className="flex items-center justify-between">
          <div className="flex-1">
            <div className="flex items-center gap-3 mb-2">
              {p.grade_img ? (
                <img 
                  src={p.grade_img} 
                  alt={p.grade || 'Grade'} 
                  className="h-8 w-8 object-contain"
                  onError={(e) => {
                    (e.target as HTMLImageElement).style.display = 'none';
                  }}
                />
              ) : (
                <div className="h-8 w-8 bg-red-600 rounded flex items-center justify-center text-white font-bold text-xs">
                  ?
                </div>
              )}
              <h4 className="text-lg font-semibold text-white">
                {p.prenom} {p.nom}
              </h4>
            </div>
            <div className="flex items-center gap-3 mt-2">
              <span className="px-2 py-1 bg-blue-500/20 text-blue-400 rounded text-xs">
                {p.grade}
              </span>
              <span className={`px-2 py-1 rounded text-xs ${
                p.actif
                  ? 'bg-green-500/20 text-green-400'
                  : 'bg-gray-700 text-gray-400'
              }`}>
                {p.actif ? 'Actif' : 'Inactif'}
              </span>
              <span className="text-xs text-gray-400">
                {p.age} ans
              </span>
            </div>
            {p.bonus && p.bonus.length > 0 && (
              <div className="mt-2 flex flex-wrap gap-2">
                {p.bonus.map((bonus, idx) => (
                  <span key={idx} className="px-2 py-1 bg-purple-500/20 text-purple-400 rounded text-xs">
                    {bonus.type}: +{bonus.valeur}
                  </span>
                ))}
              </div>
            )}
          </div>
          <button 
            onClick={(e) => {
              e.stopPropagation();
              onOpenDetails(p);
            }}
            className="ml-4 text-gray-400 hover:text-white transition-colors"
            title="Voir les détails"
          >
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 5v.01M12 12v.01M12 19v.01M12 6a1 1 0 110-2 1 1 0 010 2zm0 7a1 1 0 110-2 1 1 0 010 2zm0 7a1 1 0 110-2 1 1 0 010 2z" />
            </svg>
          </button>
        </div>
      </div>
    );
  });

  PersonnelCard.displayName = 'PersonnelCard';

  // Charger les configurations de personnel filtrées par service
  useEffect(() => {
    const loadPersonnelConfigs = async () => {
      if (!serviceId) return;

      setLoadingConfigs(true);
      try {
        const token = localStorage.getItem('token');
        if (!token) return;

        const response = await fetch(`${getApiBaseUrl(true)}/config/personnel`, {
          headers: {
            'Authorization': `Bearer ${token}`
          }
        });

        if (response.ok) {
          const data = await response.json();
          if (data.success && Array.isArray(data.data)) {
            // Filtrer par service
            const filteredConfigs = data.data.filter((config: PersonnelConfig) => 
              config.id_service === serviceId
            );
            setPersonnelConfigs(filteredConfigs);
          }
        }
      } catch (error) {
        console.error('Erreur lors du chargement des configurations de personnel:', error);
      } finally {
        setLoadingConfigs(false);
      }
    };

    if (showAddModal) {
      loadPersonnelConfigs();
    }
  }, [showAddModal, serviceId]);

  // Fonction pour recruter du personnel
  const handleRecruit = async (config: PersonnelConfig) => {
    if (!currentUser || isRecruiting) return;

    setIsRecruiting(true);
    setRecruitError(null);
    setRecruitSuccess(null);

    try {
      const token = localStorage.getItem('token');
      if (!token) {
        setRecruitError('Non authentifié');
        return;
      }

      const response = await fetch(`${getApiBaseUrl(true)}/buildings/personnel/recruit`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          configId: config.id,
          batimentId: buildingId
        })
      });

      const data = await response.json();

      if (!response.ok || !data.success) {
        setRecruitError(data.error || 'Erreur lors du recrutement');
        return;
      }

      // Mettre à jour l'argent de l'utilisateur
      if (data.data && data.data.newMoney !== undefined && currentUser) {
        dispatch(updateUser({ argents: data.data.newMoney }));
      }

      setRecruitSuccess(data.message || 'Personnel recruté avec succès');
      
      // Appeler le callback pour rafraîchir les données
      if (onPersonnelAdded) {
        setTimeout(() => {
          onPersonnelAdded();
          setShowAddModal(false);
          setRecruitSuccess(null);
        }, 1000);
      } else {
        setTimeout(() => {
          setShowAddModal(false);
          setRecruitSuccess(null);
        }, 1000);
      }
    } catch (error: any) {
      console.error('Erreur lors du recrutement:', error);
      setRecruitError('Erreur réseau lors du recrutement');
    } finally {
      setIsRecruiting(false);
    }
  };

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between mb-4">
        <h3 className="text-lg font-semibold text-white">
          Personnel ({filteredPersonnel.length}{filteredPersonnel.length !== personnel.length ? ` / ${personnel.length}` : ''})
        </h3>
        <div className="flex items-center gap-2">
          <button
            type="button"
            onClick={() => setShowOperationalPlanningModal(true)}
            className="px-4 py-2 bg-cyan-600 hover:bg-cyan-700 text-white rounded-lg text-sm font-medium transition-colors flex items-center gap-2"
          >
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
            </svg>
            Planning
          </button>
          <button 
            onClick={() => setShowAddModal(true)}
            className="px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-lg text-sm font-medium transition-colors"
          >
            + Ajouter
          </button>
        </div>
      </div>

      {loadingPersonnel ? (
        <div className="text-center py-12 text-gray-400">
          Chargement du personnel...
        </div>
      ) : personnel.length === 0 ? (
        <div className="text-center py-12 text-gray-400">
          <p className="text-lg mb-2">Aucun personnel assigné</p>
          <p className="text-sm">Cliquez sur "Ajouter" pour assigner du personnel à ce bâtiment</p>
        </div>
      ) : (
        <>
          {/* Barre de recherche et filtres */}
          <div className="space-y-3">
            <div className="flex items-center gap-2">
              <div className="relative flex-1">
                <input
                  type="text"
                  placeholder="Rechercher par nom, prénom ou grade..."
                  value={personnelSearchQuery}
                  onChange={(e) => setPersonnelSearchQuery(e.target.value)}
                  className="w-full px-4 py-2 pl-10 bg-gray-800 border border-gray-700 rounded-lg text-white placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent text-sm"
                />
                <svg
                  className="absolute left-3 top-2.5 w-4 h-4 text-gray-500"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z"
                  />
                </svg>
              </div>
              <select
                value={personnelFilterActif}
                onChange={(e) => setPersonnelFilterActif(e.target.value)}
                className="px-3 py-2 bg-gray-800 border border-gray-700 rounded-lg text-white text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              >
                <option value="all">Tous</option>
                <option value="actif">Actifs</option>
                <option value="inactif">Inactifs</option>
              </select>
              <div
                className="flex shrink-0 overflow-hidden rounded-lg border border-gray-600"
                role="group"
                aria-label="Mode d’affichage liste personnel"
              >
                <button
                  type="button"
                  onClick={() => setViewMode('compact')}
                  title="Vue compacte"
                  className={`flex items-center gap-2 px-3 py-2 text-sm font-medium ${
                    viewMode === 'compact'
                      ? 'bg-blue-600 text-white'
                      : 'bg-gray-800 text-gray-300 hover:bg-gray-700'
                  }`}
                >
                  <svg className="h-4 w-4" fill="currentColor" viewBox="0 0 24 24" aria-hidden>
                    <path d="M4 4h6v6H4V4zm10 0h6v6h-6V4zM4 14h6v6H4v-6zm10 0h6v6h-6v-6z" />
                  </svg>
                  <span className="hidden sm:inline">Compacte</span>
                </button>
                <button
                  type="button"
                  onClick={() => setViewMode('detailed')}
                  title="Vue détaillée"
                  className={`flex items-center gap-2 border-l border-gray-600 px-3 py-2 text-sm font-medium ${
                    viewMode === 'detailed'
                      ? 'bg-blue-600 text-white'
                      : 'bg-gray-800 text-gray-300 hover:bg-gray-700'
                  }`}
                >
                  <svg className="h-4 w-4" fill="currentColor" viewBox="0 0 24 24" aria-hidden>
                    <path d="M4 6h16v2H4V6zm0 5h16v2H4v-2zm0 5h16v2H4v-2z" />
                  </svg>
                  <span className="hidden sm:inline">Détaillée</span>
                </button>
              </div>
            </div>
          </div>

          {/* Liste du personnel avec hauteur fixe et scroll */}
          <div className="max-h-[500px] overflow-y-auto pr-2">
            {filteredPersonnel.length === 0 ? (
              <div className="text-center py-12 text-gray-400">
                <p className="text-lg mb-2">Aucun résultat trouvé</p>
                <p className="text-sm">Essayez de modifier vos filtres de recherche</p>
              </div>
            ) : (
              <div className={`grid ${viewMode === 'compact' ? 'grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-3' : 'grid-cols-1 gap-3'}`}>
                {paginatedPersonnel.map((p) => (
                  <PersonnelCard key={p.id} p={p} compact={viewMode === 'compact'} onOpenDetails={handleOpenPersonnelDetails} />
                ))}
              </div>
            )}
          </div>

          {/* Pagination */}
          {totalPages > 1 && (
            <div className="flex items-center justify-between pt-2 border-t border-gray-700">
              <div className="text-sm text-gray-400">
                Page {currentPage} sur {totalPages} • {filteredPersonnel.length} résultat{filteredPersonnel.length > 1 ? 's' : ''}
              </div>
              <div className="flex items-center gap-2">
                <button
                  onClick={() => setCurrentPage(prev => Math.max(1, prev - 1))}
                  disabled={currentPage === 1}
                  className="px-3 py-1 bg-gray-700 hover:bg-gray-600 disabled:bg-gray-800 disabled:text-gray-600 disabled:cursor-not-allowed text-white rounded text-sm transition-colors"
                >
                  Précédent
                </button>
                <div className="flex items-center gap-1">
                  {Array.from({ length: Math.min(5, totalPages) }, (_, i) => {
                    let pageNum: number;
                    if (totalPages <= 5) {
                      pageNum = i + 1;
                    } else if (currentPage <= 3) {
                      pageNum = i + 1;
                    } else if (currentPage >= totalPages - 2) {
                      pageNum = totalPages - 4 + i;
                    } else {
                      pageNum = currentPage - 2 + i;
                    }
                    return (
                      <button
                        key={pageNum}
                        onClick={() => setCurrentPage(pageNum)}
                        className={`px-3 py-1 rounded text-sm ${
                          currentPage === pageNum
                            ? 'bg-blue-600 text-white'
                            : 'bg-gray-700 hover:bg-gray-600 text-white'
                        }`}
                      >
                        {pageNum}
                      </button>
                    );
                  })}
                </div>
                <button
                  onClick={() => setCurrentPage(prev => Math.min(totalPages, prev + 1))}
                  disabled={currentPage === totalPages}
                  className="px-3 py-1 bg-gray-700 hover:bg-gray-600 disabled:bg-gray-800 disabled:text-gray-600 disabled:cursor-not-allowed text-white rounded text-sm transition-colors"
                >
                  Suivant
                </button>
              </div>
            </div>
          )}
        </>
      )}

      {/* Modal d'ajout de personnel */}
      {showAddModal && createPortal(
        <div className="fixed inset-0 bg-black/70 backdrop-blur-sm z-[100] flex items-center justify-center p-4" onClick={() => {
          setShowAddModal(false);
          setSearchQuery('');
        }}>
          <div 
            className="bg-gray-900 rounded-lg shadow-2xl w-full max-w-3xl max-h-[80vh] overflow-hidden border border-gray-700"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex items-center justify-between p-6 border-b border-gray-700 bg-gray-800/50">
              <h2 className="text-2xl font-bold text-white">Ajouter du personnel</h2>
              <button 
                onClick={() => setShowAddModal(false)}
                className="text-gray-400 hover:text-white transition-colors p-2 hover:bg-gray-700 rounded-lg"
              >
                <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>
            <div className="p-6">
              {/* Messages d'erreur/succès */}
              {recruitError && (
                <div className="mb-4 p-3 bg-red-500/20 border border-red-500 rounded-lg text-red-400 text-sm">
                  {recruitError}
                </div>
              )}
              {recruitSuccess && (
                <div className="mb-4 p-3 bg-green-500/20 border border-green-500 rounded-lg text-green-400 text-sm">
                  {recruitSuccess}
                </div>
              )}
              
              {/* Barre de recherche */}
              <div className="mb-4">
                <div className="relative">
                  <input
                    type="text"
                    placeholder="Rechercher un grade..."
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    className="w-full px-4 py-2 pl-10 bg-gray-800 border border-gray-700 rounded-lg text-white placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  />
                  <svg
                    className="absolute left-3 top-2.5 w-5 h-5 text-gray-500"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z"
                    />
                  </svg>
                </div>
              </div>

              <div className="overflow-y-auto max-h-[calc(80vh-220px)]">
                {loadingConfigs ? (
                  <div className="text-center py-12 text-gray-400">
                    Chargement des configurations...
                  </div>
                ) : (() => {
                  const filteredConfigs = personnelConfigs.filter(config =>
                    config.grade.toLowerCase().includes(searchQuery.toLowerCase().trim())
                  );

                  if (filteredConfigs.length === 0) {
                    return (
                      <div className="text-center py-12 text-gray-400">
                        <p className="text-lg mb-2">
                          {searchQuery ? 'Aucun résultat trouvé' : 'Aucune configuration disponible'}
                        </p>
                        <p className="text-sm">
                          {searchQuery 
                            ? 'Essayez de modifier votre recherche'
                            : 'Aucune configuration de personnel trouvée pour ce service'}
                        </p>
                      </div>
                    );
                  }

                  return (
                    <>
                      <div className="text-sm text-gray-400 mb-3">
                        {filteredConfigs.length} configuration{filteredConfigs.length > 1 ? 's' : ''} trouvée{filteredConfigs.length > 1 ? 's' : ''}
                        {searchQuery && ` pour "${searchQuery}"`}
                      </div>
                      <div className="grid grid-cols-1 gap-3">
                        {filteredConfigs.map((config) => (
                          <div key={config.id} className="bg-gray-800 border border-gray-700 rounded-lg p-4 hover:border-blue-500 transition-colors">
                            <div className="flex items-center justify-between">
                              <div className="flex-1">
                                <h4 className="text-lg font-semibold text-white">{config.grade}</h4>
                                <div className="flex items-center gap-3 mt-2">
                                  <span className="px-2 py-1 bg-green-500/20 text-green-400 rounded text-xs">
                                    Prix: {config.prix}€
                                  </span>
                                </div>
                              </div>
                              <button 
                                onClick={() => handleRecruit(config)}
                                disabled={isRecruiting}
                                className="ml-4 px-4 py-2 bg-blue-600 hover:bg-blue-700 disabled:bg-gray-600 disabled:cursor-not-allowed text-white rounded-lg text-sm font-medium transition-colors"
                              >
                                {isRecruiting ? 'Recrutement...' : 'Ajouter'}
                              </button>
                            </div>
                          </div>
                        ))}
                      </div>
                    </>
                  );
                })()}
              </div>
            </div>
          </div>
        </div>,
        document.body
      )}

      {/* Modale de détails du personnel */}
      {showPersonnelDetailsModal && selectedPersonnel && createPortal(
        <div 
          className="fixed inset-0 bg-black/70 backdrop-blur-sm z-[100] flex items-center justify-center p-4" 
          onClick={() => {
            setShowPersonnelDetailsModal(false);
            setSelectedPersonnel(null);
          }}
        >
          <div 
            className="bg-gray-900 rounded-lg shadow-2xl w-full max-w-2xl max-h-[85vh] overflow-hidden border border-gray-700"
            onClick={(e) => e.stopPropagation()}
          >
            {/* En-tête */}
            <div className="flex items-center justify-between p-6 border-b border-gray-700 bg-gray-800/50">
              <div className="flex items-center gap-4">
                {selectedPersonnel.grade_img ? (
                  <img 
                    src={selectedPersonnel.grade_img} 
                    alt={selectedPersonnel.grade || 'Grade'} 
                    className="h-12 w-12 object-contain"
                    onError={(e) => {
                      (e.target as HTMLImageElement).style.display = 'none';
                    }}
                  />
                ) : (
                  <div className="h-12 w-12 bg-red-600 rounded flex items-center justify-center text-white font-bold">
                    ?
                  </div>
                )}
                <div className="flex-1">
                  {editingNom ? (
                    <div className="flex items-center gap-2 flex-wrap">
                      <div className="flex items-center gap-2">
                        <input
                          type="text"
                          value={prenomValue}
                          onChange={(e) => setPrenomValue(e.target.value)}
                          onKeyDown={(e) => {
                            if (e.key === 'Enter') {
                              handleSaveNom();
                            } else if (e.key === 'Escape') {
                              handleCancelEditNom();
                            }
                          }}
                          className="text-2xl font-bold text-white bg-gray-700 border border-blue-500 rounded px-3 py-1 focus:outline-none focus:ring-2 focus:ring-blue-500"
                          placeholder="Prénom"
                          autoFocus
                          disabled={savingNom}
                        />
                        <input
                          type="text"
                          value={nomValue}
                          onChange={(e) => setNomValue(e.target.value)}
                          onKeyDown={(e) => {
                            if (e.key === 'Enter') {
                              handleSaveNom();
                            } else if (e.key === 'Escape') {
                              handleCancelEditNom();
                            }
                          }}
                          className="text-2xl font-bold text-white bg-gray-700 border border-blue-500 rounded px-3 py-1 focus:outline-none focus:ring-2 focus:ring-blue-500"
                          placeholder="Nom"
                          disabled={savingNom}
                        />
                      </div>
                      <button
                        onClick={handleSaveNom}
                        disabled={savingNom || !nomValue.trim() || !prenomValue.trim()}
                        className="text-green-400 hover:text-green-300 transition-colors p-1 disabled:opacity-50 disabled:cursor-not-allowed"
                        title="Sauvegarder"
                      >
                        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                        </svg>
                      </button>
                      <button
                        onClick={handleCancelEditNom}
                        disabled={savingNom}
                        className="text-red-400 hover:text-red-300 transition-colors p-1 disabled:opacity-50 disabled:cursor-not-allowed"
                        title="Annuler"
                      >
                        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                        </svg>
                      </button>
                    </div>
                  ) : (
                    <div className="flex items-center gap-2">
                      <h2 className="text-2xl font-bold text-white">
                        {selectedPersonnel.prenom} {selectedPersonnel.nom}
                      </h2>
                      <button
                        onClick={() => setEditingNom(true)}
                        className="text-gray-400 hover:text-white transition-colors p-1"
                        title="Modifier le nom"
                      >
                        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                        </svg>
                      </button>
                    </div>
                  )}
                  <p className="text-blue-400 text-sm mt-1">{selectedPersonnel.grade}</p>
                </div>
              </div>
              <button 
                onClick={() => {
                  setShowPersonnelDetailsModal(false);
                  setSelectedPersonnel(null);
                }}
                className="text-gray-400 hover:text-white transition-colors p-2 hover:bg-gray-700 rounded-lg"
              >
                <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>

            {/* Contenu */}
            <div className="p-6 overflow-y-auto max-h-[calc(85vh-120px)]">
              {/* Informations générales */}
              <div className="mb-6">
                <h3 className="text-lg font-semibold text-white mb-4">Informations générales</h3>
                <div className="grid grid-cols-2 gap-4">
                  <div className="bg-gray-800 rounded-lg p-4">
                    <p className="text-sm text-gray-400 mb-3">Statut</p>
                    <div className="space-y-2">
                      <div>
                        <span 
                          className="inline-block px-3 py-1 rounded text-sm font-medium"
                          style={getStatutGeneralColors(statutGeneral)}
                        >
                          {statutGeneral || 'Non défini'}
                        </span>
                      </div>
                      <div>
                        <span 
                          className="inline-block px-3 py-1 rounded text-sm font-medium"
                          style={getStatutPlanningColors(statutPlanning)}
                        >
                          {statutPlanning || 'Non défini'}
                        </span>
                      </div>
                    </div>
                  </div>
                  <div className="bg-gray-800 rounded-lg p-4">
                    <p className="text-sm text-gray-400 mb-2">Dernière intervention</p>
                    {derniereIntervention ? (
                      <p className="text-white text-sm font-medium">
                        {derniereIntervention.date} à {derniereIntervention.heure}
                      </p>
                    ) : (
                      <p className="text-gray-500 text-sm italic">Aucune intervention terminée</p>
                    )}
                  </div>
                  <div className="bg-gray-800 rounded-lg p-4">
                    <p className="text-sm text-gray-400 mb-1">Âge</p>
                    <p className="text-white font-semibold">{selectedPersonnel.age} ans</p>
                  </div>
                  <div className={`bg-gray-800 rounded-lg p-4 ${posteNom ? 'border border-blue-500/30' : ''}`}>
                    <p className="text-sm text-gray-400 mb-1">Poste à responsabilité</p>
                    <p className={`font-semibold ${posteNom ? 'text-blue-400' : 'text-gray-500'}`}>
                      {posteNom || 'Aucun'}
                    </p>
                  </div>
                  {selectedPersonnel.grade_code && (
                    <div className="bg-gray-800 rounded-lg p-4">
                      <p className="text-sm text-gray-400 mb-1">Grade code</p>
                      <p className="text-white font-semibold">{selectedPersonnel.grade_code}</p>
                    </div>
                  )}
                  <div className="bg-gray-800 rounded-lg p-4 sm:col-span-2">
                    <p className="text-sm text-gray-400 mb-2">
                      {t('operationalPlanning.personnelDetailGroupe')}
                    </p>
                    <select
                      className="w-full max-w-md rounded-lg border border-gray-600 bg-gray-900 px-3 py-2 text-sm text-white disabled:opacity-50"
                      value={personnelGroupeSelect}
                      disabled={savingGroupe}
                      onChange={(e) => {
                        const v = e.target.value;
                        void savePersonnelGroupe(v === '' ? '' : (v as PlanningGroupeKey));
                      }}
                      aria-label={t('operationalPlanning.personnelDetailGroupe')}
                    >
                      <option value="">{t('operationalPlanning.personnelDetailGroupeNone')}</option>
                      {PLANNING_GROUPE_KEYS.map((k) => (
                        <option key={k} value={k}>
                          {t(`operationalPlanning.filterGroupeGr${k}` as const)}
                        </option>
                      ))}
                    </select>
                    <p className="mt-2 text-xs text-gray-500">
                      {t('operationalPlanning.personnelDetailGroupeHint')}
                    </p>
                  </div>
                </div>
                {selectedPersonnel.photo && (
                  <div className="mt-4 bg-gray-800 rounded-lg p-4">
                    <p className="text-sm text-gray-400 mb-2">Photo</p>
                    <img 
                      src={selectedPersonnel.photo} 
                      alt={`${selectedPersonnel.prenom} ${selectedPersonnel.nom}`}
                      className="h-32 w-32 object-cover rounded-lg"
                      onError={(e) => {
                        (e.target as HTMLImageElement).style.display = 'none';
                      }}
                    />
                  </div>
                )}
              </div>

              {/* Bonus */}
              {selectedPersonnel.bonus && selectedPersonnel.bonus.length > 0 && (
                <div className="mb-6">
                  <h3 className="text-lg font-semibold text-white mb-4">Bonus ({selectedPersonnel.bonus.length})</h3>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                    {selectedPersonnel.bonus.map((bonus, idx) => (
                      <div key={idx} className="bg-gray-800 rounded-lg p-4 border border-purple-500/20">
                        <div className="flex items-center justify-between">
                          <span className="text-purple-400 font-medium">{bonus.type}</span>
                          <span className="text-white font-bold text-lg">+{bonus.valeur}</span>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Compétences */}
              <div className="mb-6">
                <div className="flex items-center justify-between mb-4">
                  <h3 className="text-lg font-semibold text-white">
                    Compétences
                    {loadingCompetences && <span className="text-sm text-gray-400 ml-2">(Chargement...)</span>}
                  </h3>
                </div>
                {loadingCompetences ? (
                  <div className="text-center py-8 text-gray-400">
                    Chargement des compétences...
                  </div>
                ) : (() => {
                  // Combiner toutes les compétences (personnel + grade) et éviter les doublons
                  const allCompetencesMap = new Map<number, Competence>();
                  
                  // Ajouter les compétences du grade par défaut
                  gradeDefaultCompetences.forEach(comp => {
                    if (comp.id) {
                      allCompetencesMap.set(comp.id, comp);
                    }
                  });
                  
                  // Ajouter les compétences directes du personnel (prioritaires)
                  personnelCompetences.forEach(comp => {
                    if (comp.id) {
                      allCompetencesMap.set(comp.id, comp);
                    }
                  });
                  
                  let allCompetences = Array.from(allCompetencesMap.values());
                  
                  // Filtrer par recherche
                  if (competenceSearchQuery.trim()) {
                    const searchLower = competenceSearchQuery.toLowerCase().trim();
                    allCompetences = allCompetences.filter(c =>
                      c.nom.toLowerCase().includes(searchLower) ||
                      (c.description && c.description.toLowerCase().includes(searchLower))
                    );
                  }
                  
                  const competencesNormales = allCompetences.filter(c => !c.is_specialite);
                  const specialites = allCompetences.filter(c => c.is_specialite);

                  if (allCompetences.length === 0 && !competenceSearchQuery.trim()) {
                    return (
                      <div className="bg-gray-800 rounded-lg p-6 text-center text-gray-400">
                        <p>Aucune compétence enregistrée pour ce personnel</p>
                      </div>
                    );
                  }

                  if (allCompetences.length === 0 && competenceSearchQuery.trim()) {
                    return (
                      <div className="bg-gray-800 rounded-lg p-6 text-center text-gray-400">
                        <p>Aucune compétence trouvée pour "{competenceSearchQuery}"</p>
                      </div>
                    );
                  }

                  return (
                    <div className="space-y-4">
                      {/* Barre de recherche pour les compétences */}
                      <div className="relative">
                        <input
                          type="text"
                          placeholder="Rechercher une compétence..."
                          value={competenceSearchQuery}
                          onChange={(e) => setCompetenceSearchQuery(e.target.value)}
                          className="w-full px-4 py-2 pl-10 bg-gray-800 border border-gray-700 rounded-lg text-white placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent text-sm"
                        />
                        <svg
                          className="absolute left-3 top-2.5 w-4 h-4 text-gray-500"
                          fill="none"
                          stroke="currentColor"
                          viewBox="0 0 24 24"
                        >
                          <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            strokeWidth={2}
                            d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z"
                          />
                        </svg>
                      </div>

                      {/* Compétences normales */}
                      {competencesNormales.length > 0 && (
                        <div>
                          <h4 className="text-md font-semibold text-blue-400 mb-3 flex items-center gap-2">
                            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                            </svg>
                            Compétences ({competencesNormales.length})
                          </h4>
                          <div className="max-h-[300px] overflow-y-auto pr-2">
                            <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                              {competencesNormales.map((competence) => (
                                <div 
                                  key={competence.id} 
                                  className="bg-gray-800 rounded-lg p-2.5 border border-blue-500/20 hover:border-blue-500/40 transition-colors"
                                >
                                  <div className="flex items-start justify-between">
                                    <div className="flex-1 min-w-0">
                                      <span className="font-medium text-blue-400 block text-sm truncate" title={competence.nom}>
                                        {competence.nom}
                                      </span>
                                      {competence.description && (
                                        <p className="text-xs text-gray-400 mt-1 line-clamp-2">{competence.description}</p>
                                      )}
                                    </div>
                                  </div>
                                </div>
                              ))}
                            </div>
                          </div>
                        </div>
                      )}

                      {/* Spécialités */}
                      {specialites.length > 0 && (
                        <div>
                          <h4 className="text-md font-semibold text-yellow-400 mb-3 flex items-center gap-2">
                            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 3v4M3 5h4M6 17v4m-2-2h4m5-16l2.286 6.857L21 12l-5.714 2.143L13 21l-2.286-6.857L5 12l5.714-2.143L13 3z" />
                            </svg>
                            Spécialités ({specialites.length})
                          </h4>
                          <div className="max-h-[300px] overflow-y-auto pr-2">
                            <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                              {specialites.map((competence) => (
                                <div 
                                  key={competence.id} 
                                  className="bg-gray-800 rounded-lg p-2.5 border border-yellow-500/30 bg-yellow-500/5 hover:border-yellow-500/50 hover:bg-yellow-500/10 transition-colors"
                                >
                                  <div className="flex items-start justify-between">
                                    <div className="flex-1 min-w-0">
                                      <div className="flex items-center gap-2 mb-1">
                                        <span className="font-medium text-yellow-400 text-sm truncate" title={competence.nom}>
                                          {competence.nom}
                                        </span>
                                        <span className="text-xs bg-yellow-500/20 text-yellow-400 px-1.5 py-0.5 rounded flex-shrink-0">
                                          Spé
                                        </span>
                                      </div>
                                      {competence.description && (
                                        <p className="text-xs text-gray-400 mt-1 line-clamp-2">{competence.description}</p>
                                      )}
                                    </div>
                                  </div>
                                </div>
                              ))}
                            </div>
                          </div>
                        </div>
                      )}
                    </div>
                  );
                })()}
              </div>

              {/* Informations supplémentaires */}
              <div className="border-t border-gray-700 pt-4">
                <h3 className="text-lg font-semibold text-white mb-4">Informations système</h3>
                <div className="grid grid-cols-2 gap-4 text-sm">
                  <div>
                    <p className="text-gray-400">ID Personnel</p>
                    <p className="text-white">{selectedPersonnel.id}</p>
                  </div>
                  <div>
                    <p className="text-gray-400">ID Bâtiment</p>
                    <p className="text-white">{selectedPersonnel.batiment_id || 'Non assigné'}</p>
                  </div>
                  <div>
                    <p className="text-gray-400">Date de création</p>
                    <p className="text-white">
                      {selectedPersonnel.created_at ? new Date(selectedPersonnel.created_at).toLocaleDateString('fr-FR') : 'N/A'}
                    </p>
                  </div>
                  {selectedPersonnel.updated_at && (
                    <div>
                      <p className="text-gray-400">Dernière mise à jour</p>
                      <p className="text-white">
                        {new Date(selectedPersonnel.updated_at).toLocaleDateString('fr-FR')}
                      </p>
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>,
        document.body
      )}

      <OperationalPlanningModal
        isOpen={showOperationalPlanningModal}
        onClose={() => setShowOperationalPlanningModal(false)}
        buildingId={buildingId}
        buildingName={buildingName || null}
        buildingType={buildingType}
      />
    </div>
  );
};

export default BuildingPersonnelTab;
